import { useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { makeStyles, Box } from '@material-ui/core';
import { EmptyState, Panel } from '../../../common/components';
import { StreamService } from '../services/StreamService';
import StreamList from './partials/StreamList';
import { useAlert } from '../../../common/hooks';
import { AlertSeverity } from '../../../common/providers';
import { useCallback } from 'react';

const useStyles = makeStyles(() => ({
  container: {
    height: '100%',
    minHeight: '500px',
  },
}));

function StreamPage() {
  const [t] = useTranslation();
  const { userId } = useParams<{ userId: string }>();
  const classes = useStyles();
  const { showAlert } = useAlert();

  const { data: streams } = useQuery(['streams', userId], async () => {
    const { data } = await StreamService.fetchStreams(userId);
    return data;
  });

  const queryClient = useQueryClient();
  const deleteStream = useMutation(
    async ({ userId, streamId }: { userId: string; streamId: string }) => {
      return StreamService.deleteStream(userId, streamId);
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['streams', userId]);
        showAlert({
          title: t('common.success'),
          message: t('stream.streamsDeleteSuccess'),
          severity: AlertSeverity.Success,
        });
      },
      onError: (e: Error) => {
        showAlert({
          title: t('stream.streamsDeleteError'),
          message: e.message,
          severity: AlertSeverity.Error,
        });
      },
    }
  );

  const handleDeleteStream = useCallback(
    async (streamId: string) => {
      await deleteStream.mutate({
        userId,
        streamId,
      });
    },
    [deleteStream, userId]
  );

  return (
    <Box id="Devices-container" className={classes.container}>
      <Panel id="Devices-panel" variant="contained">
        {!streams ? (
          <EmptyState variant="unavailable" message={t('device.devicesNotAvailable')} />
        ) : (
          <StreamList
            data={streams}
            onDeleteStream={handleDeleteStream}
            actionsDisabled={deleteStream.isLoading}
          />
        )}
      </Panel>
    </Box>
  );
}

export default StreamPage;
